import React, { memo, useEffect, useRef, useMemo } from 'react';
import { JsonForms } from '@jsonforms/react';
import { ThemeProvider } from '@mui/material';
import {
  materialCells,
  materialRenderers,
} from '@jsonforms/material-renderers';

import CountryGreetingControl from './Controls/CountryGreetingControl';
import { countryGreetingTester } from './Controls/countryGreetingTester';
import { jsonTextAreaTester } from './Controls/JsonTeaxtAreaControl/jsonTextAreaTester';
import JsonTextAreaControl from './Controls/JsonTeaxtAreaControl/JsonTeaxtAreaControl';
import AdditionalVocabControl from './Controls/AdditionalVocabEditorControl/AdditionalVocabControl';
import UrlFileUploadControl from './Controls/UrlFileUploadControl';
import MultiSelectControl from './Controls/MultiSelectControl';
import { theme } from './theme';
import type { FormJsonData } from '../../api/form-controller';
import { additionalVocabTester } from './Controls/JsonTeaxtAreaControl/additionalVocabTester';
import { urlFileUploadTester } from './Controls/urlFileUploadTester';
import { multiSelectTester } from './Controls/multiSelectTester';

const renderers = [
  ...materialRenderers,
  { tester: countryGreetingTester, renderer: CountryGreetingControl },
  {
    tester: jsonTextAreaTester,
    renderer: JsonTextAreaControl,
  },
  {
    tester: additionalVocabTester,
    renderer: AdditionalVocabControl,
  },
  {
    tester: urlFileUploadTester,
    renderer: UrlFileUploadControl,
  },
  {
    tester: multiSelectTester,
    renderer: MultiSelectControl,
  },
];

export type JsonBuilderProps = {
  onChange: (data: any) => void;
  formData: any;
  schemaData: FormJsonData;
  onChangeUpload?: (file: File) => void;
  options?: any;
};

const JsonFormBuilder: React.FC<JsonBuilderProps> = ({
  onChange,
  formData,
  schemaData,
  onChangeUpload,
  options,
}) => {
  const formRef = useRef<HTMLDivElement>(null);

  const memoizedOptions = useMemo(() => {
    if (!options) return [];
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.map((o: any) => `${o.value}:${o.label}`).join(',') || '']);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (
        document.activeElement instanceof HTMLInputElement &&
        document.activeElement.type === 'number'
      ) {
        event.preventDefault();
      }
    };

    const inputElements = formRef.current?.querySelectorAll(
      'input[type="number"]'
    );

    inputElements?.forEach(input => {
      (input as HTMLInputElement).addEventListener(
        'wheel',
        handleWheel as EventListener
      );
    });

    return () => {
      inputElements?.forEach(input => {
        (input as HTMLInputElement).removeEventListener(
          'wheel',
          handleWheel as EventListener
        );
      });
    };
  }, []);

  const config = useMemo(
    () => ({
      material: {
        variant: 'outlined',
        table: { disableHeader: false },
        popper: { disablePortal: false },
      },
      onChangeUpload: onChangeUpload,
      options: memoizedOptions,
    }),
    [onChangeUpload, memoizedOptions]
  );

  return (
    <div className="flex flex-col items-center py-6 px-2" ref={formRef}>
      <ThemeProvider theme={theme}>
        <JsonForms
          schema={(schemaData as any)?.jsonSchema}
          uischema={(schemaData as any)?.uiSchema}
          data={formData}
          renderers={renderers}
          cells={materialCells}
          validationMode="ValidateAndShow"
          onChange={onChange}
          config={config}
        />
      </ThemeProvider>
    </div>
  );
};

export default memo(JsonFormBuilder);
